// src/components/pdf-viewer/AnnotationTooltip.tsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Input,
  Select,
  Button,
  Space,
  Tooltip,
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  DeleteOutlined,
  TagOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { ANNOTATION_COLORS } from '../../types/annotation';

const { TextArea } = Input;

interface AnnotationTooltipProps {
  position: { top: number; left: number };
  initialComment?: string;
  initialTags?: string[];
  initialColor?: string;
  mode: 'create' | 'edit';
  onSave: (data: { comment: string; tags: string[]; color: string }) => void;
  onCancel: () => void;
  onDelete?: () => void;  // Only in edit mode
}

export const AnnotationTooltip: React.FC<AnnotationTooltipProps> = ({
  position,
  initialComment = '',
  initialTags = [],
  initialColor = ANNOTATION_COLORS.yellow,
  mode,
  onSave,
  onCancel,
  onDelete,
}) => {
  // Use refs to track initial values and only reset on actual mode/annotation change
  const [comment, setComment] = useState(initialComment);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [selectedColor, setSelectedColor] = useState(initialColor);
  const [initialized, setInitialized] = useState(false);

  // Only reset state once when component mounts or when switching annotations
  useEffect(() => {
    if (!initialized) {
      setComment(initialComment);
      setTags(initialTags);
      setSelectedColor(initialColor);
      setInitialized(true);
    }
  }, [initialized, initialComment, initialTags, initialColor]);

  const handleSave = () => {
    onSave({
      comment,
      tags,
      color: selectedColor,
    });
  };

  const handleColorSelect = (color: string) => {
    setSelectedColor(color);
  };

  // Calculate position - offset to avoid overlap with annotation
  const tooltipStyle: React.CSSProperties = {
    position: 'absolute',
    top: `${position.top + 10}px`,
    left: `${position.left}px`,
    maxWidth: '300px',
    zIndex: 9999,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  };

  // Arrow styling
  const arrowStyle: React.CSSProperties = {
    position: 'absolute',
    top: '-8px',
    left: '20px',
    width: 0,
    height: 0,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderBottom: '8px solid #fff',
  };

  const colorOptions = Object.entries(ANNOTATION_COLORS).map(([name, hex]) => ({
    name,
    hex,
  }));

  return (
    <div style={tooltipStyle}>
      <div style={arrowStyle} />
      <Card
        size="small"
        bordered={false}
        style={{
          borderRadius: '8px',
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Comment Input */}
          <div>
            <div style={{
              marginBottom: '8px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#666',
            }}>
              <MessageOutlined style={{ marginRight: '6px' }} />
              Comment
            </div>
            <TextArea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment..."
              rows={3}
              maxLength={500}
              showCount
              style={{ fontSize: '13px' }}
            />
          </div>

          {/* Tags Input */}
          <div>
            <div style={{
              marginBottom: '8px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#666',
            }}>
              <TagOutlined style={{ marginRight: '6px' }} />
              Tags
            </div>
            <Select
              mode="tags"
              value={tags}
              onChange={setTags}
              placeholder="Add tags..."
              style={{ width: '100%' }}
              size="small"
              maxTagCount="responsive"
            />
          </div>

          {/* Color Selection */}
          <div>
            <div style={{
              marginBottom: '8px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#666',
            }}>
              Color
            </div>
            <div style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
            }}>
              {colorOptions.map(({ name, hex }) => (
                <Tooltip key={name} title={name.charAt(0).toUpperCase() + name.slice(1)}>
                  <div
                    onClick={() => handleColorSelect(hex)}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: hex,
                      cursor: 'pointer',
                      border: selectedColor === hex
                        ? '3px solid #1890ff'
                        : '2px solid #d9d9d9',
                      transition: 'all 0.2s',
                      boxShadow: selectedColor === hex
                        ? '0 2px 8px rgba(24, 144, 255, 0.3)'
                        : 'none',
                    }}
                  />
                </Tooltip>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '8px',
          }}>
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSave}
              >
                {mode === 'create' ? 'Create' : 'Save'}
              </Button>
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={onCancel}
              >
                Cancel
              </Button>
            </Space>

            {mode === 'edit' && onDelete && (
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={onDelete}
              >
                Delete
              </Button>
            )}
          </div>
        </Space>
      </Card>
    </div>
  );
};
